import React, { useCallback, useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Play,
  Send,
  EyeOff,
  Trash2,
  FileSpreadsheet,
  ChevronRight,
  Sparkles,
  Award,
  UserCheck
} from "lucide-react";
import {
  getBatchById,
  generateBatch,
  publishBatch,
  unpublishBatch,
  deleteBatch
} from "../../api/reportcard/reportCardApi";
import {
  getBatchByIdPortal,
  generateBatchPortal,
  publishBatchPortal,
  unpublishBatchPortal,
  deleteBatchPortal
} from "../../api/reportcard/reportCardPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import BatchStatusBadge from "../../components/reportcard/BatchStatusBadge";
import ResultStatusBadge from "../../components/reportcard/ResultStatusBadge";
import {
  pickList,
  toId
} from "../../utils/reportcard/reportCardHelpers";
import * as UI from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const Card =
  UI.Card ||
  (({ className, children, ...props }) => (
    <div className={`bg-white dark:bg-neutral-900 border border-primary-100 rounded-2xl shadow-xs ${className}`} {...props}>
      {children}
    </div>
  ));

const Button =
  UI.Button ||
  (({ children, className, ...props }) => (
    <button
      className={`inline-flex items-center justify-center px-3.5 py-2 text-xs font-semibold rounded-xl transition-all disabled:opacity-50 cursor-pointer ${className}`}
      {...props}
    >
      {children}
    </button>
  ));

const Badge =
  UI.Badge ||
  (({ children, className }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      {children}
    </span>
  ));

const BatchDetail = ({ isPortal = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("REPORT_CARD_FULL")
    : true;
  const base = isPortal ? "/staff-panel/report-cards" : "/report-cards";

  const [batch, setBatch] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getBatchByIdPortal(id)
        : await getBatchById(id);
      setBatch(res.batch || res.data);
      setCards(pickList(res, ["cards", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load batch");
    } finally {
      setLoading(false);
    }
  }, [id, isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn, okMsg) => {
    try {
      setBusy(true);
      await fn();
      toast.success(okMsg);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-primary-600 dark:text-primary-400 gap-3">
        <Loader2 className="h-9 w-9 animate-spin text-primary-600 dark:text-primary-400" />
        <p className="text-sm font-semibold text-neutral-600 dark:text-neutral-300">Loading batch details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50/40 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate(base)}
            className="inline-flex items-center text-xs font-semibold text-neutral-500 dark:text-neutral-400 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to all batches
          </button>
        </div>

        {/* Batch Info Header Banner */}
        <Card className="p-6 sm:p-8 bg-white dark:bg-neutral-900 border-primary-100 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <BatchStatusBadge status={batch?.status} />
                <Badge className="bg-primary-50 text-primary-700 dark:text-primary-400 border border-primary-200">
                  {batch?.academicYear}
                </Badge>
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                  {batch?.examName} · {batch?.className} · {batch?.sectionName}
                </h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                  <span>
                    Template:{" "}
                    <strong className="text-neutral-800 dark:text-neutral-100 font-semibold">
                      {batch?.templateName}
                    </strong>
                  </span>
                  {batch?.gradeSchemeId?.name && (
                    <span>
                      Scheme:{" "}
                      <strong className="text-neutral-800 dark:text-neutral-100 font-semibold">
                        {batch.gradeSchemeId.name}
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {canManage && (
              <div className="flex flex-wrap items-center gap-2 pt-4 lg:pt-0 border-t lg:border-t-0 border-neutral-200 dark:border-neutral-800">
                {(batch?.status === "DRAFT" || batch?.status === "GENERATED") && (
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () =>
                          isPortal
                            ? generateBatchPortal(id)
                            : generateBatch(id),
                        "Generated from submitted marks"
                      )
                    }
                    className="bg-primary-600 hover:bg-primary-700 text-white shadow-xs"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Play className="h-4 w-4 mr-1.5" />
                    )}
                    {batch?.status === "GENERATED" ? "Regenerate" : "Generate"}
                  </Button>
                )}
                {batch?.status === "GENERATED" && (
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () =>
                          isPortal ? publishBatchPortal(id) : publishBatch(id),
                        "Published"
                      )
                    }
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <Send className="h-4 w-4 mr-1.5" />
                    )}
                    Publish
                  </Button>
                )}
                {batch?.status === "PUBLISHED" && (
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      run(
                        () =>
                          isPortal
                            ? unpublishBatchPortal(id)
                            : unpublishBatch(id),
                        "Unpublished"
                      )
                    }
                    className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:bg-neutral-950 dark:hover:bg-neutral-800"
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    ) : (
                      <EyeOff className="h-4 w-4 mr-1.5" />
                    )}
                    Unpublish
                  </Button>
                )}
                {batch?.status !== "PUBLISHED" && (
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      if (!window.confirm("Delete batch and all cards?")) return;
                      run(async () => {
                        if (isPortal) await deleteBatchPortal(id);
                        else await deleteBatch(id);
                        navigate(base);
                      }, "Deleted");
                    }}
                    className="bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200"
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {batch?.generateWarnings?.length > 0 && (
          <div className="flex items-center p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-xs font-mono">
            <AlertTriangle className="h-4 w-4 mr-2 text-amber-600 shrink-0" />
            <span>{batch.generateWarnings.join(" · ")}</span>
          </div>
        )}

        {/* Student Cards Table Card */}
        <Card className="overflow-hidden bg-white dark:bg-neutral-900 border-primary-100 shadow-sm">
          <div className="border-b border-neutral-200 dark:border-neutral-800 bg-primary-50/30 px-6 py-4 flex items-center justify-between">
            <div className="text-sm font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Generated Report Cards
            </div>
            <Badge className="bg-primary-100 text-primary-800 dark:text-primary-300">
              {cards.length} Total
            </Badge>
          </div>

          <div>
            {cards.length === 0 ? (
              <div className="p-12 text-center space-y-3">
                <p className="text-xs text-neutral-500 dark:text-neutral-400 max-w-sm mx-auto">
                  No cards yet. Click Generate after all subject marks are submitted for this class & section.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-slate-50/80 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="py-3 px-4 w-16">Rank</th>
                      <th className="py-3 px-4">Student Details</th>
                      <th className="py-3 px-4 text-center">Percentage</th>
                      <th className="py-3 px-4 text-center">Grade</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-neutral-800">
                    {cards.map((c) => (
                      <tr key={toId(c)} className="hover:bg-primary-50/20 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-neutral-500 dark:text-neutral-400">
                          {c.rankInSection ? `#${c.rankInSection}` : "—"}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-neutral-900 dark:text-neutral-100">
                            {c.studentName}
                          </div>
                          <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                            Adm: {c.admissionNo}
                            {c.rollNumber != null ? ` · Roll: ${c.rollNumber}` : ""}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center font-semibold text-neutral-800 dark:text-neutral-100">
                          {c.overallPercentage}%
                        </td>
                        <td className="py-3.5 px-4 text-center font-bold text-primary-700 dark:text-primary-400">
                          {c.overallGrade}
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <ResultStatusBadge status={c.resultStatus} />
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            type="button"
                            className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:bg-primary-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                            onClick={() => navigate(`${base}/cards/${toId(c)}`)}
                          >
                            View / Print
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BatchDetail;